import { ClockIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";

import classNames from "../../../utils/classNames";

/**
 * Ticking "how long ago" cell: minutes in green, hours in grey turning amber after four, days in
 * red. Re-renders once a minute so a list left open stays honest.
 */
export default function AgeCell({ created }: { created: string }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(intervalId);
  }, []);

  const ageDisplay = useMemo(() => {
    const createdMs = new Date(created).getTime();
    if (Number.isNaN(createdMs)) {
      return { display: "—", color: "text-gray-500" };
    }

    const totalMins = Math.max(0, Math.floor((now - createdMs) / 60000));
    const days = Math.floor(totalMins / 1440);
    const hrs = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;

    if (days > 0) {
      return { display: `${days}d ${hrs}h`, color: "text-red-600" };
    }

    if (hrs > 0) {
      return {
        display: `${hrs}h ${mins}m`,
        color: hrs >= 4 ? "text-amber-600" : "text-gray-700",
      };
    }

    return { display: `${mins}m`, color: "text-green-600" };
  }, [created, now]);

  return (
    <div className="flex items-center gap-1">
      <ClockIcon className={classNames("h-3.5 w-3.5", ageDisplay.color)} />
      <span className={classNames("text-xs font-semibold tabular-nums", ageDisplay.color)}>{ageDisplay.display}</span>
    </div>
  );
}
